import { Global, ThemeProvider } from '@emotion/react';
import { createTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { createServer, Response } from 'miragejs';
import { createRoot } from 'react-dom/client';
import { Provider as StorageProvider } from 'react-redux';
import Theme from 'App.theme';
import { store } from 'store';
import styles from 'styles/Global';
import appInfo from '../package.json';
import App from './App';
//import { makeServer } from './server';
import Auth0ProviderWithHistory from 'components/Auth0ProviderWithHistory';
import { BrowserRouter } from 'react-router-dom';

/* -------------------------------------------------------------------------- */
/*                       Set up sentry for observability                      */
/* -------------------------------------------------------------------------- */
Sentry.init({
  dsn: process.env.REACT_APP_WEB_SENTRY_DSN,
  integrations: [new Integrations.BrowserTracing()],
  release: appInfo.version,
  dist: `${appInfo.version}`,
  environment:
    process.env.NODE_ENV === 'development'
      ? 'localDev'
      : // Note that the below only works if the API uses the correct naming conventions -> you'll need to check this for each project
      `${process.env.REACT_APP_SADL_API_HOST}`.includes('dev')
      ? 'dev'
      : 'ppd',
  tracesSampleRate: 0.7,
});

/* -------------------------------------------------------------------------- */
/*                        Set up miragejs for API mocks                       */
/* -------------------------------------------------------------------------- */

/* --------------------- Use mocks for local development -------------------- */
/*
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_REAL_SERVER !== 'true') {
  makeServer({ environment: 'development' });
}
*/

/* ------------------------- Use mocks for e2e tests ------------------------ */
// @ts-expect-error
if (window.Cypress) {
  // If your app makes requests to domains other than / (the current domain), add them
  // here so that they are also proxied from your app to the handleFromCypress function.
  // For example: let otherDomains = ["https://my-backend.herokuapp.com/"]

  const otherDomains: string[] = ['https://savi-data-uploader-api.dev.api.dev.savi.maxkelsen.com/v1/'];

  const methods = ['get', 'put', 'patch', 'post', 'delete'];

  createServer({
    environment: 'test',
    routes() {
      for (const domain of ['/', ...otherDomains]) {
        for (const method of methods) {
          // @ts-expect-error
          this[method](`${domain}*`, async (schema, request) => {
            // @ts-expect-error
            const [status, headers, body]: any = await window.handleFromCypress(request);
            return new Response(status, headers, body);
          });
        }
      }

      // If your central server has any calls to passthrough(), you'll need to duplicate them here
      // this.passthrough('https://analytics.google.com')
      // this.passthrough('https://savi-data-uploader-api.dev.api.dev.savi.maxkelsen.com/v1/sets/asset/');
      this.passthrough('https://www.googleapis.com/identitytoolkit/v3/relyingparty/verifyPassword');
      this.passthrough('https://www.googleapis.com/identitytoolkit/v3/relyingparty/getAccountInfo');
      this.passthrough('https://securetoken.googleapis.com/v1/token');
      this.passthrough('https://firebasestorage.googleapis.com');
    },
  });
}

const theme = createTheme({
  palette: {
    primary: { main: Theme.colours.primary, dark: Theme.colours.primaryDark },
    secondary: { main: Theme.greys.GREY2 },
    success: { main: Theme.status.APPROVED },
    error: { main: Theme.status.REJECTED },
    warning: { main: Theme.status.MODIFIED },
    info: { main: Theme.colours.info },
  },
});

const container = document.getElementById('root');
if (container)
  createRoot(container).render(
    <>
      <Global styles={styles} />
      <ThemeProvider theme={theme}>
        <StorageProvider store={store}>
          <BrowserRouter>
            <Auth0ProviderWithHistory>
              <App />
            </Auth0ProviderWithHistory>
          </BrowserRouter>
        </StorageProvider>
      </ThemeProvider>
    </>,
  );
